import React, { useEffect, useState } from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import {
  ExternalLink,
  Heading,
  IconArrowHead,
  IconEnvelopeClassic,
  IconGlobal,
  IconLocation,
  InlineTextButton,
  NamedLink,
} from '../../components';
import classNames from 'classnames';
import { copyToClipboard } from '../../util/clipboard';
import api from '../../api';
import IconUserType from '../../components/IconUserType/IconUserType';
import IconPhoneCircle from '../../components/IconPhoneCircle/IconPhoneCircle';
import { formatLocation } from '../../util/mapbox';
import useInterfaceLang from '../../hooks/useInterfaceLang';
import { getDisplatName } from '../../util/userHelpers';
import { USER_TYPE } from '../../constants/user.constants';
import InfoLink from './InfoLink';

import css from './ListingPage.module.css';

const SectionAuthorMaybe = props => {
  const { language } = useInterfaceLang();

  const { listing, focusSignal } = props;

  if (!listing.author) {
    return null;
  }

  const [isRevealInProgress, setIsRevealInProgress] = useState(false);
  const [fullPhoneNumber, setFullPhoneNumber] = useState();
  const [isCopied, setIsCopied] = useState(false);
  const { publicData } = listing.author.attributes.profile;
  const { companyEmail, businessAddress, companyWebsite, phoneNumberShort, userType } = publicData;
  const displayName = getDisplatName(listing.author);

  const onShowPhoneNumber = async () => {
    try {
      setIsRevealInProgress(true);
      const response = await api.listings.revealContact(listing.id.uuid);
      const { phoneNumber } = response?.data || {};

      setFullPhoneNumber(phoneNumber);
    } finally {
      setIsRevealInProgress(false);
    }
  };

  const onTrackStatistics = async listingStatisticsType => {
    try {
      await api.listings.incrementListingStatistics(listing.id.uuid, { listingStatisticsType });
    } catch (e) {}
  };

  useEffect(() => {
    if (isCopied) {
      const t = setTimeout(() => setIsCopied(false), 1000);
      return () => clearTimeout(t);
    }
  }, [isCopied]);

  const companyWebsiteWithProtocol = !/^https?:\/\//i.test(companyWebsite)
    ? `https://${companyWebsite}`
    : companyWebsite;

  const formattedLocation = businessAddress?.address
    ? formatLocation(businessAddress?.address, language)
    : businessAddress?.address;

  return (
    <section id="author" className={css.sectionAuthor}>
      <Heading as="h2" rootClassName={css.sectionHeadingWithExtraMargin}>
        <FormattedMessage id="ListingPage.offeredBy" />
      </Heading>
      <div className={css.sectionAuthorContent}>
        <div className={css.sectionDisplayName}>{displayName}</div>
        {phoneNumberShort ? (
          <div className={css.phoneNumber}>
            <span>
              <IconPhoneCircle className={css.iconPhone} />
              {fullPhoneNumber ? (
                <button
                  type="button"
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(fullPhoneNumber);
                      setIsCopied(true);
                    } catch (e) {
                      copyToClipboard(fullPhoneNumber);
                      setIsCopied(true);
                    }
                  }}
                >
                  {fullPhoneNumber}
                  {isCopied ? (
                    <div className={css.copied}>
                      <FormattedMessage id="Global.copied" />
                    </div>
                  ) : null}
                </button>
              ) : (
                `${phoneNumberShort}...`
              )}
            </span>
            {fullPhoneNumber ? (
              <ExternalLink
                className={classNames({ [css.highlightedBtn]: focusSignal })}
                href={`tel:${fullPhoneNumber}`}
              >
                <FormattedMessage id="Global.call" />
              </ExternalLink>
            ) : (
              <InlineTextButton
                type="button"
                className={classNames({ [css.highlightedBtn]: focusSignal })}
                onClick={onShowPhoneNumber}
                inProgress={isRevealInProgress}
              >
                <FormattedMessage id="Global.show" />
              </InlineTextButton>
            )}
          </div>
        ) : null}
        <div className={css.sectionUserType}>
          <IconUserType className={css.iconUserType} />
          <span>
            <FormattedMessage id={`General.user.type.${userType}`} />
          </span>
        </div>

        {companyWebsiteWithProtocol || companyEmail ? (
          <div className={css.contact}>
            {formattedLocation && userType === USER_TYPE.BUSINESS_SELLER ? (
              <InfoLink
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                  formattedLocation
                )}`}
                label={formattedLocation}
                icon={IconLocation}
              />
            ) : null}
            {companyWebsiteWithProtocol ? (
              <InfoLink
                href={companyWebsiteWithProtocol}
                label={companyWebsite}
                icon={IconGlobal}
                onClick={() => onTrackStatistics('viewsWebsiteCount')}
              />
            ) : null}
            {companyEmail ? (
              <InfoLink
                icon={IconEnvelopeClassic}
                href={`mailto:${companyEmail}`}
                label={companyEmail}
              />
            ) : null}
          </div>
        ) : null}
        <NamedLink className={css.link} name="ProfilePage" params={{ id: listing.author.id.uuid }}>
          <FormattedMessage id="Global.moreAds" /> <IconArrowHead direction="right" />
        </NamedLink>
      </div>
    </section>
  );
};

export default SectionAuthorMaybe;
